import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getLlmConfig,
  listLlmSuggestions,
  requestLlmSuggestion,
  type LlmAdvisorAlternative,
  type LlmAdvisorPayload,
  type LlmConfigPayload,
  type LlmRecommendation,
  type LlmSuggestion,
  type LlmSuggestionsList,
  type LlmRouteName,
} from "../../../api/llm";

export interface AppliedAlternativeSelection {
  suggestion: LlmSuggestion;
  alternative: LlmAdvisorAlternative;
}

export interface LlmSuggestionPanelProps {
  reviewItemId: string;
  /** Когда reviewer кликает Apply на альтернативе. Parent должен включить
   *  edit-mode и подмержить field_overrides в draft. */
  onApply: (selection: AppliedAlternativeSelection) => void;
  /** Доп. контекст к промпту (force_register и пр.). Опционально. */
  extraContext?: Record<string, unknown>;
}

export function LlmSuggestionPanel({
  reviewItemId,
  onApply,
  extraContext,
}: LlmSuggestionPanelProps) {
  const qc = useQueryClient();

  const configQ = useQuery<LlmConfigPayload>({
    queryKey: ["llm-config", reviewItemId],
    queryFn: async () => {
      const r = await getLlmConfig(reviewItemId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    enabled: Boolean(reviewItemId),
  });

  const suggestionsQ = useQuery<LlmSuggestionsList>({
    queryKey: ["llm-suggestions", reviewItemId],
    queryFn: async () => {
      const r = await listLlmSuggestions(reviewItemId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    enabled: Boolean(reviewItemId),
    refetchInterval: (q) =>
      q.state.data?.suggestions?.some((s: LlmSuggestion) => s.status === "pending")
        ? 5000
        : false,
  });

  const recommendation: LlmRecommendation | null =
    configQ.data?.recommendation ?? null;

  // Локальный override модели/маршрута, если reviewer руками поменял.
  const [overrideModelId, setOverrideModelId] = useState<string | null>(null);
  const [overrideRoute, setOverrideRoute] = useState<LlmRouteName | null>(null);
  const [withJudge, setWithJudge] = useState<boolean>(true);

  const selectedModelId =
    overrideModelId ?? recommendation?.advisor.model_id ?? "";
  const selectedRoute =
    overrideRoute ?? recommendation?.advisor.route ?? undefined;

  const selectedEntry = useMemo(
    () => configQ.data?.models.find((m) => m.model_id === selectedModelId),
    [configQ.data, selectedModelId],
  );
  const availableRoutes = selectedEntry?.routes ?? [];

  const isOverridden = Boolean(overrideModelId || overrideRoute);

  const judgeModelId = recommendation?.judge.model_id ?? null;
  const judgeRoute = recommendation?.judge.route ?? undefined;
  const judgeAvailable = Boolean(judgeModelId && judgeRoute);

  const suggestMut = useMutation({
    mutationFn: async () => {
      const r = await requestLlmSuggestion({
        review_item_id: reviewItemId,
        model_id: selectedModelId,
        route: selectedRoute,
        n_alternatives: 3,
        extra_context: extraContext,
        with_judge: withJudge && judgeAvailable,
        judge_model_id: withJudge && judgeAvailable ? judgeModelId! : undefined,
        judge_route: withJudge && judgeAvailable ? judgeRoute : undefined,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["llm-suggestions", reviewItemId] });
    },
  });

  // Hotkey L → Suggest. Не срабатывает в input/textarea.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "l" && e.key !== "L") return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      ) {
        return;
      }
      if (!selectedModelId || suggestMut.isPending) return;
      e.preventDefault();
      suggestMut.mutate();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedModelId, suggestMut]);

  const suggestions = suggestionsQ.data?.suggestions ?? [];
  const hasPending = suggestions.some((s) => s.status === "pending");

  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>LLM-советчик</span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span
          className="mt-text-tight mt-muted"
          style={{ textTransform: "none", letterSpacing: 0 }}
        >
          горячая клавиша L
        </span>
      </div>
      <div className="rf-section__body mt-llm">
        <div className="mt-llm__header">
          <div className="mt-llm__selector">
            <label htmlFor="llm-model">Модель</label>
            <select
              id="llm-model"
              className="mt-llm__select"
              value={selectedModelId}
              onChange={(e) => {
                setOverrideModelId(e.target.value || null);
                setOverrideRoute(null);
              }}
              disabled={configQ.isLoading || configQ.isError}
            >
              {(configQ.data?.models ?? []).map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.label}
                </option>
              ))}
            </select>

            <label htmlFor="llm-route">Маршрут</label>
            <select
              id="llm-route"
              className="mt-llm__select"
              value={selectedRoute ?? ""}
              onChange={(e) =>
                setOverrideRoute((e.target.value || null) as LlmRouteName | null)
              }
              disabled={availableRoutes.length === 0}
            >
              {availableRoutes.map((r) => (
                <option key={r.name} value={r.name}>
                  {routeLabel(r.name)}
                </option>
              ))}
            </select>
          </div>

          <label
            className="mt-llm__judge-toggle"
            title={
              judgeAvailable
                ? `peer-review через ${labelForModelId(configQ.data, judgeModelId!)}`
                : "Judge недоступен — нет ключа для рекомендованной модели"
            }
          >
            <input
              type="checkbox"
              checked={withJudge && judgeAvailable}
              disabled={!judgeAvailable}
              onChange={(e) => setWithJudge(e.target.checked)}
            />
            with judge
          </label>

          <button
            type="button"
            className="mt-btn mt-btn--sm mt-btn--primary"
            onClick={() => suggestMut.mutate()}
            disabled={
              !selectedModelId ||
              suggestMut.isPending ||
              configQ.isLoading
            }
          >
            {suggestMut.isPending ? "Генерируем…" : "Suggest"}
          </button>
        </div>

        {recommendation && (
          <div className="mt-llm__rec">
            <span aria-hidden>🤖</span>
            <span>
              Рекомендовано:{" "}
              <strong>{labelForModelId(configQ.data, recommendation.advisor.model_id)}</strong>
              {recommendation.judge?.model_id ? (
                <>
                  {" "}
                  · судья:{" "}
                  <strong>
                    {labelForModelId(configQ.data, recommendation.judge.model_id)}
                  </strong>
                </>
              ) : null}{" "}
              — {recommendation.reason}
            </span>
            {isOverridden && (
              <button
                className="mt-llm__rec-reset"
                type="button"
                onClick={() => {
                  setOverrideModelId(null);
                  setOverrideRoute(null);
                }}
              >
                ↺ Сбросить к рекомендации
              </button>
            )}
          </div>
        )}

        {suggestMut.isError && (
          <div className="mt-llm-pending" style={{ color: "var(--red-700)" }}>
            Ошибка: {(suggestMut.error as Error).message}
          </div>
        )}

        {hasPending && (
          <div className="mt-llm-pending">
            <span>⏳</span> Ждём ответ модели…
          </div>
        )}

        <div className="mt-llm__alts">
          {suggestions.length === 0 && !suggestMut.isPending && (
            <div className="mt-llm-empty">
              Ещё нет предложений. Нажмите <kbd>L</kbd> или Suggest.
            </div>
          )}
          {suggestions.map((s) => (
            <SuggestionBlock key={s.suggestion_id} suggestion={s} onApply={onApply} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SuggestionBlock({
  suggestion,
  onApply,
}: {
  suggestion: LlmSuggestion;
  onApply: (sel: AppliedAlternativeSelection) => void;
}) {
  if (suggestion.status === "pending") {
    return (
      <div className="mt-llm-card mt-llm-card--abstain">
        <div className="mt-llm-card__head">
          <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
            pending
          </span>
          <span>{suggestion.model}</span>
          <span>·</span>
          <span>{relTime(suggestion.created_at)}</span>
        </div>
      </div>
    );
  }

  if (suggestion.status === "failed") {
    return (
      <div className="mt-llm-card mt-llm-card--abstain">
        <div className="mt-llm-card__head">
          <span className="mt-badge mt-badge--error mt-badge--no-dot">
            failed
          </span>
          <span>{suggestion.model}</span>
          <span>·</span>
          <span>{relTime(suggestion.created_at)}</span>
        </div>
        <div className="mt-llm-card__reason">
          {suggestion.error_message ?? "—"}
        </div>
      </div>
    );
  }

  const advisor = suggestion.advisor_payload as LlmAdvisorPayload;
  const summary = advisor?.summary;
  const alternatives = advisor?.alternatives ?? [];
  const judge = suggestion.judge_payload;
  const winnerIndex = judge?.winner_index ?? null;
  const scoreByIndex = new Map<number, number>();
  const rationaleByIndex = new Map<number, string>();
  if (judge?.scores) {
    for (const s of judge.scores) {
      scoreByIndex.set(s.alternative_index, s.score);
      rationaleByIndex.set(s.alternative_index, s.rationale);
    }
  }

  if (summary?.abstain) {
    return (
      <div className="mt-llm-card mt-llm-card--abstain">
        <div className="mt-llm-card__head">
          <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
            abstain
          </span>
          <span>{suggestion.model}</span>
          <span>·</span>
          <span>{relTime(suggestion.created_at)}</span>
        </div>
        <div className="mt-llm-card__reason">
          Модель воздержалась: {summary.abstain_reason ?? "—"}
        </div>
      </div>
    );
  }

  return (
    <>
      {alternatives.map((alt) => {
        const isWinner = winnerIndex === alt.index;
        const score = scoreByIndex.get(alt.index);
        const judgeRationale = rationaleByIndex.get(alt.index);
        return (
          <div
            key={`${suggestion.suggestion_id}:${alt.index}`}
            className={
              "mt-llm-card" + (isWinner ? " mt-llm-card--winner" : "")
            }
          >
            <div className="mt-llm-card__head">
              <span className="mt-badge mt-badge--info mt-badge--no-dot">
                A{alt.index}
              </span>
              <span>{suggestion.model}</span>
              <ConfidenceBar value={alt.confidence} />
              {score != null && (
                <span
                  className="mt-llm-card__score"
                  title={
                    suggestion.judge_model
                      ? `judge: ${suggestion.judge_model}`
                      : "judge score"
                  }
                >
                  ⚖ {score.toFixed(2)}
                </span>
              )}
              {isWinner && (
                <span className="mt-badge mt-badge--success mt-badge--no-dot">
                  победитель
                </span>
              )}
              <span>·</span>
              <span>{relTime(suggestion.created_at)}</span>
              {suggestion.applied_decision_id && (
                <span className="mt-badge mt-badge--success mt-badge--no-dot">
                  применено
                </span>
              )}
            </div>
            <div className="mt-llm-card__target">{alt.target_text}</div>
            {alt.reasoning && (
              <div className="mt-llm-card__reason">{alt.reasoning}</div>
            )}
            {judgeRationale && (
              <div
                className="mt-llm-card__reason"
                style={{ fontStyle: "italic" }}
              >
                ⚖ {judgeRationale}
              </div>
            )}
            <div className="mt-llm-card__actions">
              <button
                type="button"
                className={
                  "mt-btn mt-btn--sm " +
                  (isWinner ? "mt-btn--primary" : "mt-btn--ghost")
                }
                onClick={() => onApply({ suggestion, alternative: alt })}
                disabled={Boolean(suggestion.applied_decision_id)}
              >
                Apply A{alt.index}
              </button>
              <button
                type="button"
                className="mt-btn mt-btn--sm mt-btn--ghost"
                onClick={() => navigator.clipboard?.writeText(alt.target_text)}
              >
                Copy
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  const filled = Math.max(0, Math.min(5, Math.round(value * 5)));
  return (
    <span
      className="mt-llm-conf"
      title={`confidence ${value.toFixed(2)}`}
      aria-label={`confidence ${value.toFixed(2)}`}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={"mt-llm-conf__seg" + (i < filled ? " is-on" : "")}
        />
      ))}
    </span>
  );
}

function routeLabel(name: LlmRouteName): string {
  if (name === "openai_direct") return "OpenAI direct";
  if (name === "anthropic_direct") return "Anthropic direct";
  if (name === "deepseek_direct") return "DeepSeek direct";
  if (name === "routerai") return "RouterAI";
  return name;
}

function labelForModelId(
  config: LlmConfigPayload | undefined,
  modelId: string,
): string {
  return (
    config?.models.find((m) => m.model_id === modelId)?.label ?? modelId
  );
}

function relTime(iso: string): string {
  try {
    const d = new Date(iso);
    const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return `${diffSec}s назад`;
    if (diffSec < 3600) return `${Math.round(diffSec / 60)}m назад`;
    if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h назад`;
    return d.toLocaleDateString("ru-RU");
  } catch {
    return iso;
  }
}
